import { formatDateTime } from '../../shared/date-time';
import { formatEventOccurrenceName, formatPropertyValue } from './format';
import { CountryBadge } from './GeoUi';
import { PlatformBadge } from './PlatformUi';
import type { AnalyticsEventRecord } from './types';

type EventSummaryVariant = 'timeline' | 'explorer';
type EventDetailsVariant = 'inline' | 'panel';

export function EventOccurrenceSummary(props: { event: AnalyticsEventRecord; variant: EventSummaryVariant }): React.JSX.Element {
  const event = props.event;
  if (props.variant === 'timeline') {
    return (
      <span className="event-occurrence-summary timeline">
        <time>{formatElapsedClock(event.elapsedMs)}</time>
        <i aria-hidden="true" />
        <span className="event-summary-copy">
          <strong>{formatEventOccurrenceName(event)}</strong>
          <small><PlatformBadge code={event.platformCode} /> <CountryBadge countryCode={event.countryCode} /> {event.categoryName} · {event.eventCode} · seq {event.sequence}</small>
          <code>{event.properties.map(formatNamedProperty).join(' · ')}</code>
        </span>
      </span>
    );
  }

  return (
    <span className="event-occurrence-summary explorer">
      <span className="event-summary-copy">
        <strong>{formatEventOccurrenceName(event)}</strong>
        <small><PlatformBadge code={event.platformCode} /> <CountryBadge countryCode={event.countryCode} /> {event.categoryName} · {event.eventCode} · {formatDateShort(event.clientTime)}</small>
      </span>
      <span className="event-summary-timing">
        <strong>{formatElapsedHuman(event.elapsedMs)}</strong>
        <small>seq {event.sequence}</small>
      </span>
    </span>
  );
}

export function EventOccurrenceDetails(props: { event: AnalyticsEventRecord; variant: EventDetailsVariant; id?: string }): React.JSX.Element {
  const event = props.event;
  const className = props.variant === 'inline' ? 'event-details event-details-inline' : 'event-details event-details-panel';
  const heading = props.variant === 'inline'
    ? <h3>{formatEventOccurrenceName(event)}</h3>
    : <h2>{formatEventOccurrenceName(event)}</h2>;

  return (
    <section id={props.id} className={className} aria-label={`${formatEventOccurrenceName(event)} details`}>
      <div className="event-details-heading"><div><span className="eyebrow">Event details</span>{heading}</div><code>{event.eventCode}/v{event.version}</code></div>
      <div className="event-details-context">
        <Detail label="Category" value={`${event.categoryName} · ${event.categoryKey} · ${event.categoryCode}`} />
        <Detail label="Event" value={`${event.eventKey} · ${event.eventCode}/v${event.version}`} />
        <Detail label="Event ID" value={event.eventId} code />
        <Detail label="Sequence" value={String(event.sequence)} />
        <Detail label="Installation ID" value={event.installationId} code />
        <Detail label="Session ID" value={event.sessionId} code />
        <Detail label="Session elapsed" value={formatElapsedLong(event.elapsedMs)} />
        <Detail label="Platform" value={<PlatformBadge code={event.platformCode} />} />
        <Detail label="Country" value={<CountryBadge countryCode={event.countryCode} />} />
        <Detail label="Geo lookup" value={event.geoStatus} />
        {event.sourceIp !== undefined && <Detail label="Source IP" value={event.sourceIp ?? '—'} code />}
        <Detail label="Client time" value={formatDateLong(event.clientTime)} />
        <Detail label="Server received" value={formatDateLong(event.serverTime)} />
        <Detail label="Level" value={event.levelId ?? '—'} />
        <Detail label="Clock skew" value={event.clockSkewed ? 'Detected' : 'No'} warning={event.clockSkewed} />
      </div>
      <div className="event-details-properties">
        <div className="event-details-section-heading"><span className="eyebrow">Schema-resolved data</span><strong>{event.properties.length} properties</strong></div>
        {event.properties.map(renderDetailedProperty)}
        {event.properties.length === 0 && <p>No event-specific properties.</p>}
      </div>
      <details className="raw-event event-details-raw"><summary>Raw compact wire properties</summary><pre>{JSON.stringify(event.rawProperties, null, 2)}</pre></details>
    </section>
  );
}

function Detail(props: { label: string; value: React.ReactNode; code?: boolean; warning?: boolean }): React.JSX.Element {
  const className = props.warning === true ? 'event-detail-value warning' : 'event-detail-value';
  return <span><small>{props.label}</small>{props.code === true ? <code className={className}>{props.value}</code> : <strong className={className}>{props.value}</strong>}</span>;
}

function renderDetailedProperty(property: AnalyticsEventRecord['properties'][number]): React.JSX.Element {
  const metadata = [property.key, property.type, property.unit].filter(hasText).join(' · ');
  return <div className="event-details-property" key={property.key}><span><strong>{property.displayName}</strong><small>{metadata}</small></span><code>{formatPropertyValue(property)}</code></div>;
}

function hasText(value: string | null): value is string {
  return value !== null && value !== '';
}

function formatNamedProperty(property: AnalyticsEventRecord['properties'][number]): string {
  return `${property.displayName}: ${formatPropertyValue(property)}`;
}

function formatElapsedClock(milliseconds: number): string {
  const totalSeconds = Math.floor(milliseconds / 1000);
  return `${String(Math.floor(totalSeconds / 60)).padStart(2, '0')}:${String(totalSeconds % 60).padStart(2, '0')}`;
}

function formatElapsedHuman(milliseconds: number): string {
  const totalSeconds = Math.floor(milliseconds / 1000);
  return `${Math.floor(totalSeconds / 60)}m ${totalSeconds % 60}s`;
}

function formatElapsedLong(milliseconds: number): string {
  return `${formatElapsedClock(milliseconds)} · ${new Intl.NumberFormat().format(milliseconds)} ms`;
}

function formatDateShort(value: string): string {
  return formatDateTime(value);
}

function formatDateLong(value: string): string {
  return formatDateTime(value, 'second');
}
